/*
  Digital download agreement modal.

  Behavior:
    - Any <buy-button> rendered with `data-digital-product` triggers a confirm modal
      before the product is added to cart.
    - Click is intercepted in document capture phase, BEFORE Maestrooo's BuyButton
      class handler runs (which is registered on the buy-button element itself).
    - When the user checks the agreement and confirms, we re-fire the original click;
      a one-shot _digitalAgreed flag tells our capture handler to let it through.
    - If the user cancels or closes the modal, nothing is added.

  Marks of digital products live in Liquid via `product.template_suffix == 'digital'`.
  Set the product's template to "digital" in Shopify admin to trigger this flow.
*/
(() => {
  let modal = null;
  let pendingTrigger = null;
  let lastFocus = null;

  const buildModal = () => {
    const m = document.createElement('div');
    m.className = 'digital-agreement';
    m.setAttribute('aria-hidden', 'true');
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-labelledby', 'digital-agreement-title');
    m.innerHTML = `
      <div class="digital-agreement__backdrop" data-da-close></div>
      <div class="digital-agreement__panel" role="document">
        <div class="digital-agreement__icon" aria-hidden="true">!</div>
        <h2 class="digital-agreement__title" id="digital-agreement-title">Digital download — please confirm</h2>
        <p class="digital-agreement__body">
          You are purchasing a <strong>digital download</strong>. By adding this item to your cart, you acknowledge:
        </p>
        <ul class="digital-agreement__list">
          <li>No physical product will be shipped to you.</li>
          <li>You will receive access to digital files only — there is no tangible item.</li>
          <li>Due to the nature of digital goods, all sales are <strong>final and non-refundable</strong> once the order is placed.</li>
          <li>You <strong>may not resell, redistribute, or sell prints</strong> made from these files. <a href="https://neattools.com/pages/digital-file-license-usage-terms" target="_blank" rel="noopener">Read the full license terms here</a>.</li>
        </ul>
        <label class="digital-agreement__check">
          <input type="checkbox" data-da-checkbox>
          <span>I understand this is a digital product, no physical item will be shipped, and the purchase is non-refundable. I have read and agree to the <a href="https://neattools.com/pages/digital-file-license-usage-terms" target="_blank" rel="noopener" data-da-link>digital file license &amp; usage terms</a>.</span>
        </label>
        <div class="digital-agreement__actions">
          <a href="#" class="digital-agreement__alt" data-da-alt hidden>Looking for the physical version? Click here &rarr;</a>
          <div class="digital-agreement__actions-primary">
            <button type="button" class="button button--secondary" data-da-cancel>Cancel</button>
            <button type="button" class="button button--primary" data-da-confirm disabled>Add to cart</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(m);
    return m;
  };

  const ensureModal = () => {
    if (!modal) modal = buildModal();
    return modal;
  };

  const openModal = (trigger) => {
    pendingTrigger = trigger;
    const m = ensureModal();
    const cb = m.querySelector('[data-da-checkbox]');
    const confirmBtn = m.querySelector('[data-da-confirm]');
    const altBtn = m.querySelector('[data-da-alt]');
    cb.checked = false;
    confirmBtn.disabled = true;

    const buyBtn = trigger.closest('buy-button');
    const altUrl = buyBtn ? buyBtn.getAttribute('data-alt-product-url') : null;
    if (altUrl) {
      altBtn.href = altUrl;
      altBtn.removeAttribute('hidden');
    } else {
      altBtn.setAttribute('hidden', '');
    }

    m.setAttribute('aria-hidden', 'false');
    document.body.classList.add('digital-agreement-open');
    lastFocus = document.activeElement;
    setTimeout(() => cb.focus(), 50);
  };

  const closeModal = () => {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('digital-agreement-open');
    pendingTrigger = null;
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (_) {}
    }
  };

  const confirmAndProceed = () => {
    const trigger = pendingTrigger;
    closeModal();
    if (!trigger) return;
    const buyBtn = trigger.closest('buy-button');
    if (buyBtn) buyBtn._digitalAgreed = true;
    setTimeout(() => trigger.click(), 0);
  };

  document.addEventListener('click', (e) => {
    const buyBtn = e.target.closest('buy-button[data-digital-product]');
    if (!buyBtn) return;
    if (buyBtn._digitalAgreed) {
      buyBtn._digitalAgreed = false;
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    openModal(e.target);
  }, true);

  document.addEventListener('click', (e) => {
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    if (e.target.closest('[data-da-close], [data-da-cancel]')) {
      e.preventDefault();
      closeModal();
    } else if (e.target.closest('[data-da-confirm]')) {
      e.preventDefault();
      const cb = modal.querySelector('[data-da-checkbox]');
      if (!cb || !cb.checked) return;
      confirmAndProceed();
    }
  });

  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-da-checkbox]')) return;
    const confirmBtn = modal && modal.querySelector('[data-da-confirm]');
    if (confirmBtn) confirmBtn.disabled = !e.target.checked;
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-da-link]')) {
      e.stopPropagation();
    }
  }, true);

  document.addEventListener('keydown', (e) => {
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  });
})();
